import React, { useCallback, useEffect, useState } from "react";
import { withRefluxStore } from "../../../Stores/index.js";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Modal,
  ActionButton,
  PosthogStore,
  useIsFeatureEnabled
} from "@talentbait/shared-components";
import Actions from "../../../Actions.js";
import {
  ProfileContainer,
  EmptyStateText,
  ProfileContent,
  BottomButtonsContainer,
  deleteButtonStyles,
  PageHeader,
  addReqsButtonStyles,
  ProfileNavigation,
  NavTitle,
  CloseBtn,
  ProfileBackground,
  NotPermittedBox,
  EmptyStateHeader,
  Skeleton,
  CategoryItem,
  SidebarBox,
  SidebarIcon,
  ProfilesSidebar,
  LayoutIcon,
  SideHeader,
  ConfigureButton,
  iconTextButtonStyles
} from "./Profile.styled.js";
import Requirement from "./requirement/Requirement.jsx";
import { Group } from "../../../styles/layout/index.js";
import { deleteModalStyles } from "../modals/Modals.styled.js";
import { X as XIcon } from "@untitledui/icons/X";
import { FlipBackward } from "@untitledui/icons/FlipBackward";
import { LayoutLeft } from "@untitledui/icons/LayoutLeft";
import ProfilesStore from "../Profile.store.js";
import { DeleteConfirm } from "../modals/DeleteConfirm.jsx";
import logger from "../../../utils/LoggerFrontend";
import ProfileCategories from "./profile-categories/ProfileCategories.jsx";
import { requirementNeedsConfiguration } from "../Profile.utils.js";
import ProfileSummary from "./profile-summary/ProfileSummary.jsx";
import { TabsContextProvider } from "../../../context/Tabs.context.jsx";

const Profile = ({
  // store
  userProfile,
  posthogInstance,
  profile,
  profileMeta,
  domainSkills,
  // router
  match,
  history
}) => {
  const { t } = useTranslation();
  const hasPermission = useIsFeatureEnabled("is-talentbait-team", posthogInstance);

  const profileId = match.params.id;

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDeletingProfile, setIsDeletingProfile] = useState(false);
  const [openCards, setOpenCards] = useState(new Set([0])); // First card open by default

  const handleDeleteProfile = useCallback(() => setIsDeletingProfile((prev) => !prev), []);
  const handleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);

  const goToDashboard = useCallback(() => history.push(`/profiles`), [history]);

  useEffect(() => {
    if (userProfile?.organizationId) {
      Actions.getProfileByOrganizationId(profileId, userProfile.organizationId);
    }
  }, [userProfile?.organizationId, profileId, history]);

  useEffect(() => {
    if (profile.jobCategoryId) {
      Actions.getCategoriesByJobCategoryId(profile.jobCategoryId);
      Actions.getDomainSkillsByJobCategoryId(profile.jobCategoryId);
    }
  }, [profile.jobCategoryId]);

  const openCategoryCard = useCallback(
    (category) => {
      const reqIndex = profile.requirements.findIndex((req) => req.category === category);
      if (reqIndex === -1) return;

      setOpenCards((prevOpenCards) => {
        const newOpenCards = new Set(prevOpenCards);
        if (newOpenCards.has(reqIndex)) {
          newOpenCards.delete(reqIndex); // Close it
        } else {
          newOpenCards.add(reqIndex); // Open it
        }

        return newOpenCards;
      });
    },
    [profile.requirements]
  );

  const handleCardToggle = useCallback((index, isOpen) => {
    setOpenCards((prev) => {
      const newSet = new Set(prev);

      if (isOpen) newSet.add(index);
      else newSet.delete(index);

      return newSet;
    });
  }, []);

  const handleConfirmDeleteProfile = useCallback(() => {
    logger.log("Profile:handleConfirmDeleteProfile", "deleting profile", profile);

    const ref = Actions.deleteProfile.completed.listen(() => {
      Actions.filterProfileById(profile.id);
    });

    Actions.deleteProfile(profile.id);
    setIsDeletingProfile(false);
    history.push("/profiles");

    return () => {
      ref();
    };
  }, [profile, history]);

  const renderNavigation = (
    <ProfileNavigation>
      <NavTitle>{t("Profile Details")}</NavTitle>

      <Group direction="row" align="center">
        <ActionButton
          variant="create"
          onClick={() => undefined}
          text={t("Create job page")}
          styles={addReqsButtonStyles}
          isActive
        />
        <ActionButton
          variant="create"
          onClick={() => undefined}
          text={t("Create campaign")}
          styles={addReqsButtonStyles}
        />

        <CloseBtn onClick={goToDashboard}>
          <XIcon />
        </CloseBtn>
      </Group>
    </ProfileNavigation>
  );

  if (!hasPermission) {
    return (
      <ProfileBackground>
        {renderNavigation}
        <NotPermittedBox>
          <EmptyStateHeader>{t("You do not have permission to view this page.")}</EmptyStateHeader>
          <EmptyStateText>
            {t("Please contact your administrator if you believe this is an error.")}
          </EmptyStateText>
        </NotPermittedBox>
      </ProfileBackground>
    );
  }

  if (profileMeta === "failed") {
    return (
      <ProfileBackground>
        {renderNavigation}
        <NotPermittedBox>
          <EmptyStateHeader>{t("Failed to load the profile.")}</EmptyStateHeader>
          <EmptyStateText mb="1rem">
            {t("Please contact your administrator if you believe this is an error.")}
          </EmptyStateText>

          <Group direction="row" width="100%" justify="center">
            <ActionButton
              variant="link"
              icon={<FlipBackward />}
              onClick={goToDashboard}
              text={t("Return to dashboard.")}
              styles={iconTextButtonStyles}
            />
          </Group>
        </NotPermittedBox>
      </ProfileBackground>
    );
  }

  const isLoaded = profileMeta === "loaded";

  return (
    <TabsContextProvider defaultTab="expected">
      <ProfileBackground>
        {renderNavigation}

        <ProfileContainer>
          {/* 
            Column 1 - Sidebar  
          */}
          <ProfilesSidebar $isOpen={sidebarOpen}>
            <SidebarIcon onClick={handleSidebar}>
              <LayoutLeft />
            </SidebarIcon>
            <SidebarBox>
              <SideHeader>{t("Added")}</SideHeader>
              {isLoaded
                ? profile.requirements.map((req) => {
                    const needsConfig = requirementNeedsConfiguration(req);
                    return (
                      <CategoryItem
                        key={req.category}
                        onClick={() => openCategoryCard(req.category)}
                        $needsConfig={needsConfig}
                      >
                        <span>{req.category}</span>
                        {needsConfig && <ConfigureButton>{t("Configure")}</ConfigureButton>}
                      </CategoryItem>
                    );
                  })
                : Array.from({ length: 5 }).map((_, idx) => <Skeleton key={idx} />)}
            </SidebarBox>
          </ProfilesSidebar>

          {/* 
            Column 2 - Content  
          */}
          <ProfileContent>
            <LayoutIcon onClick={handleSidebar}>
              <LayoutLeft />
            </LayoutIcon>

            <ProfileSummary isLoaded={isLoaded} />

            <Group gap="40px">
              <PageHeader>{t("Requirements")}</PageHeader>
              {isLoaded ? (
                profile.requirements?.length === 0 ? (
                  <EmptyStateText>
                    {t(
                      "This profile doesn't have any requirements yet. Start building your profile by adding requirements that define the skills, experience, and qualifications needed."
                    )}
                  </EmptyStateText>
                ) : (
                  profile.requirements.map((req, index) => (
                    <Requirement
                      key={req.category}
                      index={index}
                      req={req}
                      domainSkills={domainSkills}
                      isOpen={openCards.has(index)}
                      onToggle={handleCardToggle}
                    />
                  ))
                )
              ) : (
                <>
                  <Group gap="24px">
                    <Skeleton height="40px" />
                    <Skeleton height="80px" />
                  </Group>

                  {Array.from({ length: 4 }).map((_, idx) => (
                    <Skeleton key={idx} height="40px" />
                  ))}
                </>
              )}

              {isLoaded && (
                <BottomButtonsContainer>
                  <ActionButton
                    variant="filter"
                    onClick={handleDeleteProfile}
                    text={t("Delete Profile")}
                    styles={deleteButtonStyles}
                    useSmall
                  />
                </BottomButtonsContainer>
              )}
            </Group>
          </ProfileContent>

          {/* 
            Column 3 - Categories  
          */}
          <ProfileCategories />
        </ProfileContainer>

        <Modal
          isOpen={isDeletingProfile}
          setIsOpen={setIsDeletingProfile}
          styles={deleteModalStyles}
        >
          <DeleteConfirm
            header={t("Are you sure you want to delete this profile?")}
            confirm={handleConfirmDeleteProfile}
            cancel={handleDeleteProfile}
          />
        </Modal>
      </ProfileBackground>
    </TabsContextProvider>
  );
};

export default withRouter(
  withRefluxStore(
    Profile,
    [PosthogStore, ProfilesStore],
    ["userProfile", "posthogInstance", "profile", "profileMeta", "domainSkills"]
  )
);
